import { useSelector } from 'react-redux';
import {
  findActivePilotProgram,
  getPilotAgentEnteredIntake,
  getPilotSelfPlanIntakeForm,
  isPilotFirstPlanWaived,
  resolveAdvisorBrandingMode,
  type PilotCapability,
} from '@shared';
import { selectFeatureFlags } from '@/model/selectors';

export function useActivePilotProgram() {
  const featureFlags = useSelector(selectFeatureFlags);
  return findActivePilotProgram(featureFlags);
}

export function useIsPilotRestricted(capability: PilotCapability) {
  const program = useActivePilotProgram();
  return !!program?.restrictedCapabilities.includes(capability);
}

export function useAdvisorBrandingMode() {
  const featureFlags = useSelector(selectFeatureFlags);
  return resolveAdvisorBrandingMode(featureFlags);
}

/**
 * The self-plan intake form that replaces "Fill Intake Form" for this advisor, or null
 * when their program doesn't offer one.
 */
export function usePilotSelfPlanIntakeForm() {
  const featureFlags = useSelector(selectFeatureFlags);
  return getPilotSelfPlanIntakeForm(featureFlags);
}

/**
 * The agent-entered intake configuration for this advisor, or null when their program has none.
 *
 * The only question the feature asks on the client. There is no separate flag: it is non-null
 * exactly when the advisor carries their program's flag, so turning that off is the whole
 * rollout control.
 */
export function usePilotAgentEnteredIntake() {
  const featureFlags = useSelector(selectFeatureFlags);
  return getPilotAgentEnteredIntake(featureFlags);
}

/**
 * Whether the advisor's program excuses creating a first client during onboarding.
 *
 * The wizard's skip affordances hang off this. A program whose first-plan options cannot produce
 * a client has to be able to skip the step, or its advisors cannot finish onboarding at all.
 */
export function usePilotFirstPlanWaived() {
  const featureFlags = useSelector(selectFeatureFlags);
  return isPilotFirstPlanWaived(featureFlags);
}
